/**
 * Color Utilities
 * HEX/RGB conversion, lightening, darkening, contrast, and color manipulation
 * Compatible with Web and React Native
 */
/**
 * Validates a HEX color string
 */
export function isValidHex(hex) {
    return /^#?([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex);
}
/**
 * Normalizes a HEX color to 6-character format with #
 */
export function normalizeHex(hex) {
    let normalized = hex.replace('#', '');
    if (normalized.length === 3) {
        normalized = normalized
            .split('')
            .map((c) => c + c)
            .join('');
    }
    return `#${normalized.toUpperCase()}`;
}
/**
 * Converts a HEX color to RGB
 */
export function hexToRgb(hex) {
    if (!isValidHex(hex)) {
        return null;
    }
    const normalized = normalizeHex(hex).replace('#', '');
    return {
        r: parseInt(normalized.substring(0, 2), 16),
        g: parseInt(normalized.substring(2, 4), 16),
        b: parseInt(normalized.substring(4, 6), 16),
    };
}
/**
 * Converts RGB to HEX color
 */
export function rgbToHex(r, g, b) {
    const toHex = (c) => {
        const clamped = Math.max(0, Math.min(255, Math.round(c)));
        const hex = clamped.toString(16);
        return hex.length === 1 ? '0' + hex : hex;
    };
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}
/**
 * Converts RGB to HSL
 */
export function rgbToHsl(r, g, b) {
    r /= 255;
    g /= 255;
    b /= 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r:
                h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
                break;
            case g:
                h = ((b - r) / d + 2) / 6;
                break;
            case b:
                h = ((r - g) / d + 4) / 6;
                break;
        }
    }
    return {
        h: Math.round(h * 360),
        s: Math.round(s * 100),
        l: Math.round(l * 100),
    };
}
/**
 * Converts HSL to RGB
 */
export function hslToRgb(h, s, l) {
    h /= 360;
    s /= 100;
    l /= 100;
    let r, g, b;
    if (s === 0) {
        r = g = b = l;
    }
    else {
        const hue2rgb = (p, q, t) => {
            if (t < 0)
                t += 1;
            if (t > 1)
                t -= 1;
            if (t < 1 / 6)
                return p + (q - p) * 6 * t;
            if (t < 1 / 2)
                return q;
            if (t < 2 / 3)
                return p + (q - p) * (2 / 3 - t) * 6;
            return p;
        };
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
    }
    return {
        r: Math.round(r * 255),
        g: Math.round(g * 255),
        b: Math.round(b * 255),
    };
}
/**
 * Lightens a HEX color by a percentage (0-100)
 */
export function lighten(hex, amount) {
    const rgb = hexToRgb(hex);
    if (!rgb)
        return hex;
    const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
    hsl.l = Math.min(100, hsl.l + amount);
    const newRgb = hslToRgb(hsl.h, hsl.s, hsl.l);
    return rgbToHex(newRgb.r, newRgb.g, newRgb.b);
}
/**
 * Darkens a HEX color by a percentage (0-100)
 */
export function darken(hex, amount) {
    const rgb = hexToRgb(hex);
    if (!rgb)
        return hex;
    const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
    hsl.l = Math.max(0, hsl.l - amount);
    const newRgb = hslToRgb(hsl.h, hsl.s, hsl.l);
    return rgbToHex(newRgb.r, newRgb.g, newRgb.b);
}
/**
 * Applies a shade/tint shift to a color
 * Positive percent = tint (lighter), negative = shade (darker)
 */
export function shade(hex, percent) {
    if (percent > 0) {
        return lighten(hex, percent);
    }
    return darken(hex, Math.abs(percent));
}
/**
 * Returns the luminance of a color (0-1)
 * Used for contrast calculations
 */
export function getLuminance(hex) {
    const rgb = hexToRgb(hex);
    if (!rgb)
        return 0;
    const [r, g, b] = [rgb.r, rgb.g, rgb.b].map((c) => {
        c /= 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
/**
 * Returns a contrast color (black or white) for readable text on a background
 */
export function getContrastColor(hex) {
    const luminance = getLuminance(hex);
    return luminance > 0.179 ? '#000000' : '#FFFFFF';
}
/**
 * Calculates the contrast ratio between two colors
 * WCAG requires 4.5:1 for normal text, 3:1 for large text
 */
export function getContrastRatio(hex1, hex2) {
    const lum1 = getLuminance(hex1);
    const lum2 = getLuminance(hex2);
    const lighter = Math.max(lum1, lum2);
    const darker = Math.min(lum1, lum2);
    return (lighter + 0.05) / (darker + 0.05);
}
/**
 * Checks if two colors have sufficient contrast for accessibility
 */
export function hasAccessibleContrast(foreground, background, level = 'AA', largeText = false) {
    const ratio = getContrastRatio(foreground, background);
    if (level === 'AAA') {
        return largeText ? ratio >= 4.5 : ratio >= 7;
    }
    // AA level
    return largeText ? ratio >= 3 : ratio >= 4.5;
}
/**
 * Generates a random HEX color
 */
export function generateRandomColor() {
    const hex = Math.floor(Math.random() * 16777215).toString(16);
    return `#${hex.padStart(6, '0').toUpperCase()}`;
}
/**
 * Generates a random color with good contrast against a background
 */
export function generateContrastingColor(background) {
    let attempts = 0;
    let color = generateRandomColor();
    while (attempts < 50 && !hasAccessibleContrast(color, background)) {
        color = generateRandomColor();
        attempts++;
    }
    return color;
}
/**
 * Mixes (blends) two HEX colors together
 * @param ratio 0 = all hex1, 1 = all hex2, 0.5 = equal mix
 */
export function mix(hex1, hex2, ratio = 0.5) {
    const rgb1 = hexToRgb(hex1);
    const rgb2 = hexToRgb(hex2);
    if (!rgb1 || !rgb2)
        return hex1;
    const r = Math.round(rgb1.r * (1 - ratio) + rgb2.r * ratio);
    const g = Math.round(rgb1.g * (1 - ratio) + rgb2.g * ratio);
    const b = Math.round(rgb1.b * (1 - ratio) + rgb2.b * ratio);
    return rgbToHex(r, g, b);
}
/**
 * Creates a color with adjusted opacity (returns rgba string)
 */
export function withOpacity(hex, opacity) {
    const rgb = hexToRgb(hex);
    if (!rgb)
        return hex;
    const alpha = Math.max(0, Math.min(1, opacity));
    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}
/**
 * Inverts a color (RGB inversion)
 */
export function invertColor(hex) {
    const rgb = hexToRgb(hex);
    if (!rgb)
        return hex;
    return rgbToHex(255 - rgb.r, 255 - rgb.g, 255 - rgb.b);
}
/**
 * Converts a color to grayscale
 */
export function grayscale(hex) {
    const rgb = hexToRgb(hex);
    if (!rgb)
        return hex;
    // Use luminosity method for more accurate grayscale
    const gray = Math.round(0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b);
    return rgbToHex(gray, gray, gray);
}
/**
 * Adjusts the saturation of a color
 * @param amount Positive to increase, negative to decrease (-100 to 100)
 */
export function saturate(hex, amount) {
    const rgb = hexToRgb(hex);
    if (!rgb)
        return hex;
    const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
    hsl.s = Math.max(0, Math.min(100, hsl.s + amount));
    const newRgb = hslToRgb(hsl.h, hsl.s, hsl.l);
    return rgbToHex(newRgb.r, newRgb.g, newRgb.b);
}
/**
 * Generates a color palette from a base color
 */
export function generatePalette(baseColor, steps = 5) {
    const stepAmount = 100 / (steps + 1);
    const light = [];
    const dark = [];
    for (let i = 1; i <= steps; i++) {
        light.push(lighten(baseColor, stepAmount * i));
        dark.push(darken(baseColor, stepAmount * i));
    }
    return { light, dark };
}
/**
 * Generates complementary color
 */
export function getComplementary(hex) {
    const rgb = hexToRgb(hex);
    if (!rgb)
        return hex;
    const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
    hsl.h = (hsl.h + 180) % 360;
    const newRgb = hslToRgb(hsl.h, hsl.s, hsl.l);
    return rgbToHex(newRgb.r, newRgb.g, newRgb.b);
}
/**
 * Generates triadic colors
 */
export function getTriadic(hex) {
    const rgb = hexToRgb(hex);
    if (!rgb)
        return [hex, hex, hex];
    const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
    const colors = [hex, '', ''];
    for (let i = 1; i <= 2; i++) {
        const newHsl = { ...hsl, h: (hsl.h + 120 * i) % 360 };
        const newRgb = hslToRgb(newHsl.h, newHsl.s, newHsl.l);
        colors[i] = rgbToHex(newRgb.r, newRgb.g, newRgb.b);
    }
    return colors;
}
/**
 * Parses various color formats to HEX
 */
export function parseColor(color) {
    // Already HEX
    if (isValidHex(color)) {
        return normalizeHex(color);
    }
    // RGB format: rgb(255, 255, 255)
    const rgbMatch = color.match(/^rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/i);
    if (rgbMatch) {
        return rgbToHex(parseInt(rgbMatch[1], 10), parseInt(rgbMatch[2], 10), parseInt(rgbMatch[3], 10));
    }
    // RGBA format: rgba(255, 255, 255, 0.5)
    const rgbaMatch = color.match(/^rgba\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*[\d.]+\s*\)$/i);
    if (rgbaMatch) {
        return rgbToHex(parseInt(rgbaMatch[1], 10), parseInt(rgbaMatch[2], 10), parseInt(rgbaMatch[3], 10));
    }
    return null;
}
//# sourceMappingURL=color.js.map